import {
  BillingInfo,
  BillingProvider,
  ProductKey,
  SubscriptionState,
  SubscriptionStatus,
} from '@/gql/generated';

type PlanKeySource = {
  productKey?: ProductKey | null;
  legacyProductKey?: ProductKey | null;
};

// SuperVision keys are not members of the app's ProductKey enum yet (the
// pinned app schema predates them); values match the billing-service enum.
const SUPERVISION_PRODUCT_KEYS: ReadonlySet<string> = new Set([
  'COMPANY_OBSERVATION',
  'WORKFORCE_OBSERVATION',
  'STANDARD_SUPERVISION',
  'PRACTICE_SUPERVISION',
  'WORKFORCE_PRACTICE_MANAGEMENT',
  'ORGANIZATIONAL_MATTER_MANAGEMENT',
]);

/**
 * The product key the legacy app should branch on (BIO-1992, epic BIO-1991).
 *
 * billing-service adds `legacyProductKey` next to `productKey` on the
 * getBillingInfo core plan (BIO-1999): SuperVision plans get their mapped
 * legacy key, every other plan gets its own key back. `productKey` itself
 * keeps returning the true SuperVision key because Hexagon resolves its
 * gating tier from it.
 *
 * The translation is only honored for SuperVision plans — i.e. migrated
 * customers. Native legacy keys keep reading `productKey` unconditionally, so
 * a server-side remap of a legacy key (PLUS → ELITE) cannot silently change
 * what a non-migrated customer sees the day billing-service deploys: `isElite`
 * feeds the migration-flow recommendation and billable surfaces, and whether
 * legacy Plus should be treated as Elite is a product decision, not a side
 * effect (PR #4086 review).
 *
 * `legacyProductKey` is null until billing-service ships, so a SuperVision
 * plan without it falls back to its own (unrecognised) key — today's
 * behaviour.
 */
export const effectiveProductKey = (
  plan?: PlanKeySource | null
): ProductKey | undefined => {
  if (!plan?.productKey) {
    return undefined;
  }

  if (SUPERVISION_PRODUCT_KEYS.has(plan.productKey)) {
    return plan.legacyProductKey ?? plan.productKey;
  }

  return plan.productKey;
};

/**
 * The plan key guidance-session support options should match on (BIO-2033).
 *
 * `SupportOptions` topics are keyed by legacy product keys (`LITE`/`CORE`/
 * `PLUS`/`ELITE`), but the call sites matched them against the price-plans
 * catalog plan — whose `productKey` is the true SuperVision key for a
 * migrated customer. No topic ever matched, so the guidance-session and
 * guidance-follow-up options silently disappeared from the support modal
 * and every `findSupportOptionForPlan` consumer. Prefer the billing-info
 * core plan's effective (legacy) key; when billing info has not loaded yet,
 * fall back to the catalog key so behaviour is byte-identical to before.
 */
export const effectiveSupportPlanKey = (
  billingInfo: BillingInfo | undefined,
  catalogProductKey: ProductKey | undefined
): ProductKey | undefined =>
  effectiveProductKey(billingInfo?.coreSubscription?.plan) ?? catalogProductKey;

export const resolveIsElite = (
  billingInfo?: BillingInfo
): boolean | undefined =>
  effectiveProductKey(billingInfo?.coreSubscription?.plan) ===
    ProductKey.Elite ||
  billingInfo?.addonSubscriptions.some(
    (sub) => sub.plan.productKey === ProductKey.Elite
  );

export const resolveIsDedicated = (
  billingInfo?: BillingInfo
): boolean | undefined => {
  const coreKey = effectiveProductKey(billingInfo?.coreSubscription?.plan);

  return (
    coreKey === ProductKey.Dedicated ||
    coreKey === ProductKey.Core ||
    billingInfo?.addonSubscriptions.some(
      (sub) =>
        sub.plan.productKey === ProductKey.Dedicated ||
        sub.plan.productKey === ProductKey.Core
    )
  );
};

type CoreSubscriptionSource = {
  active?: boolean | null;
  provider?: BillingProvider | null;
  state?: SubscriptionState | null;
  plan?: PlanKeySource | null;
};

type ResolvedCorePlan = ProductKey.Core | ProductKey.Elite | ProductKey.Pause;

const coreOrElite = (plan: PlanKeySource): ProductKey.Core | ProductKey.Elite =>
  effectiveProductKey(plan) === ProductKey.Elite
    ? ProductKey.Elite
    : ProductKey.Core;

/**
 * The plan a getBillingInfo core subscription puts a company on (BIO-2085),
 * matching what a Stripe company in the same state gets today.
 *
 * A RECURLY subscription: billing-service only returns live rows (ACTIVE,
 * TRIALING, PAST_DUE, PAUSED, and CANCELED before the period ends), so a
 * returned row is the plan whatever `active` says. A past-due Stripe company
 * keeps its plan too. PAUSED → Pause, as a paused Stripe company resolves to
 * Pause. A null `state` is treated as live, not paused.
 *
 * Any other provider keeps the Active/Trialing rule: an inactive subscription
 * is no plan.
 *
 * Elite → Elite. Every other core plan (Core, Dedicated, or a key the app does
 * not recognise yet) → Core, the same fallback the Stripe path in
 * usePlanAccess uses. No core subscription, or one with no plan → undefined:
 * callers must not treat it as Core.
 */
export const resolveCorePlan = (
  coreSubscription?: CoreSubscriptionSource | null
): ResolvedCorePlan | undefined => {
  if (!coreSubscription?.plan) {
    return undefined;
  }

  if (coreSubscription.provider === BillingProvider.Recurly) {
    return coreSubscription.state === SubscriptionState.Paused
      ? ProductKey.Pause
      : coreOrElite(coreSubscription.plan);
  }

  return coreSubscription.active
    ? coreOrElite(coreSubscription.plan)
    : undefined;
};

/**
 * The plan for a company that has a Stripe customer but no live Stripe core
 * plan, such as a company migrated to Recurly whose Stripe core subscription
 * is cancelled (BIO-2085). Only a RECURLY answer counts. A STRIPE answer is
 * the same Stripe data the caller already found no live plan in (for example
 * a past-due Stripe subscription), so it gets today's outcome: undefined, and
 * the caller falls back exactly as before.
 */
export const resolveMigratedCorePlan = (
  coreSubscription?: CoreSubscriptionSource | null
): ResolvedCorePlan | undefined =>
  coreSubscription?.provider === BillingProvider.Recurly
    ? resolveCorePlan(coreSubscription)
    : undefined;

// Mirrors billing-service's isCoreProductKey: a Stripe subscription with one
// of these product codes is the company's core plan.
const STRIPE_CORE_PRODUCT_CODES: ReadonlySet<string> = new Set([
  'LITE',
  'ADVISOR',
  'STORAGE',
  'BUSINESS',
  'PAUSE',
  'CORE',
  'PLUS',
  'ELITE',
  'ELITE2',
  'ELITE3',
  'PREMIUM_PEOPLE',
  'DEDICATED',
  'GUIDANCE',
  'BOLT',
  ...SUPERVISION_PRODUCT_KEYS,
]);

/**
 * True when a Stripe subscription list has a live (Active or Trialing) core
 * plan. A company with one never needs getBillingInfo. Live add-ons alone do
 * not count: a migrated company keeps billing seats and add-ons in Stripe.
 */
export const hasLiveStripeCorePlan = (
  subscriptions: Array<{
    productCode?: string | null;
    status?: string | null;
  }> = []
): boolean =>
  subscriptions.some(
    (sub) =>
      !!sub.productCode &&
      STRIPE_CORE_PRODUCT_CODES.has(sub.productCode) &&
      (sub.status === SubscriptionStatus.Active ||
        sub.status === SubscriptionStatus.Trialing)
  );
